import { useState } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import {
  MdClose,
  MdStar,
  MdCheckCircle,
  MdCancel,
  MdBlock,
  MdAccountBalanceWallet,
} from 'react-icons/md';
import { formatDate, formatINR, statusBadgeClass } from '../utils/format';

export default function ContributorDetailModal({
  detail,
  wallet,
  onClose,
  onContributorUpdated,
  onApprove,
  onReject,
  onSuspend,
  onActivate,
  onStartManualKyc,
  busy = false,
}) {
  const [updatingTypeId, setUpdatingTypeId] = useState(null);
  const [manualAadhaar, setManualAadhaar] = useState('');
  const [manualPan, setManualPan] = useState('');
  const [approvingKyc, setApprovingKyc] = useState(false);

  if (!detail || detail === 'loading') return null;

  const handleContributorTypeChange = async (id, isStandard) => {
    setUpdatingTypeId(id);
    try {
      const res = await api.patch(`/admin/contributors/${id}/type`, { is_standard: isStandard });
      if (res.data?.success) {
        toast.success(res.data.message || (isStandard ? 'Marked as Standard Contributor' : 'Marked as Normal Contributor'));
        if (onContributorUpdated) {
          onContributorUpdated({ id, is_standard: isStandard });
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update contributor type');
    } finally {
      setUpdatingTypeId(null);
    }
  };

  const handleManualKycClick = () => {
    if (onStartManualKyc) {
      onStartManualKyc({
        contributor: detail,
        manualAadhaar,
        manualPan,
      });
      return;
    }

    // Direct manual KYC approval if no external modal handler is passed
    const trimmedAadhaar = String(manualAadhaar || '').trim();
    const aadhaarDigits = trimmedAadhaar.replace(/\D/g, '');
    const isMaskedFormat = /^[X*x]{4}[\s-]?[X*x]{4}[\s-]?\d{4}$/.test(trimmedAadhaar);

    if (!trimmedAadhaar) {
      toast.error('Please enter the Contributor Aadhaar number');
      return;
    }
    if (aadhaarDigits.length !== 12 && (!isMaskedFormat || aadhaarDigits.length < 4)) {
      toast.error('Invalid Aadhaar format. Must be a 12-digit number (e.g. 1234 5678 6405 or XXXX XXXX 6405)');
      return;
    }

    const cleanPan = String(manualPan || '').trim().toUpperCase();
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!cleanPan) {
      toast.error('Please enter the Contributor PAN number');
      return;
    }
    if (!panRegex.test(cleanPan)) {
      toast.error('Invalid PAN format. Must be 10 alphanumeric characters (e.g. CLHPC2704F)');
      return;
    }

    const formattedAadhaar = aadhaarDigits.length === 12
      ? `XXXX XXXX ${aadhaarDigits.slice(-4)}`
      : trimmedAadhaar;

    performDirectKycApproval(detail.id, {
      aadhaar_number: formattedAadhaar,
      pan_number: cleanPan,
    });
  };

  const performDirectKycApproval = async (contributorId, payload) => {
    setApprovingKyc(true);
    try {
      const res = await api.post(`/admin/contributors/${contributorId}/approve-kyc`, payload);
      if (res.data?.success) {
        toast.success(res.data.message || 'KYC approved successfully');
        if (onContributorUpdated) {
          onContributorUpdated(res.data.data);
        }
      } else {
        toast.error(res.data?.message || 'KYC approval failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'KYC approval failed. Please try again.');
    } finally {
      setApprovingKyc(false);
    }
  };

  const walletStats = wallet?.stats;

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1050 }}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 960,
          width: '94%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Modal Header */}
        <div
          className="modal-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 28px',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
            background: 'var(--card)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <img
              src={detail.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(detail.name || 'Contributor')}&background=EF233C&color=fff&bold=true`}
              alt={detail.name}
              style={{ width: 50, height: 50, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border)', background: '#fff' }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(detail.name || 'Contributor')}&background=EF233C&color=fff&bold=true`;
              }}
            />
            <div>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 17 }}>
                {detail.name}
                <span className={`badge ${statusBadgeClass(detail.status)}`}>{detail.status}</span>
              </h3>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Contributor ID: #{detail.id}</span>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}><MdClose /></button>
        </div>

        {/* Modal Body */}
        <div
          className="modal-body"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px 32px',
            scrollbarGutter: 'stable',
          }}
        >
          {/* Profile Image Section */}
          <div className="detail-row" style={{ marginBottom: 16 }}>
            <span className="detail-label">Profile Image</span>
            <div>
              <img
                src={detail.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(detail.name || 'Contributor')}&background=EF233C&color=fff&bold=true`}
                alt={detail.name || 'Contributor Profile'}
                style={{
                  width: 200,
                  height: 200,
                  maxWidth: '100%',
                  borderRadius: 12,
                  objectFit: 'cover',
                  border: '1px solid var(--border)',
                  background: '#fff',
                  display: 'block',
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(detail.name || 'Contributor')}&background=EF233C&color=fff&bold=true`;
                }}
              />
            </div>
          </div>

          {/* 2-Column Info Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '20px 32px',
            }}
          >
            <div className="detail-row">
              <span className="detail-label">Email</span>
              <span style={{ wordBreak: 'break-all' }}>{detail.email || '-'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Phone</span>
              <span>{detail.phone || '-'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Category of interest</span>
              <span>{detail.category_interest || '-'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Assigned Location</span>
              <span>{[detail.mandal ? `${detail.mandal} Mandal` : null, detail.district ? `${detail.district} Dist.` : null, detail.state].filter(Boolean).join(', ') || [detail.city, detail.state].filter(Boolean).join(', ') || '-'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Mandal Incharge</span>
              <span>
                {detail.is_mandal_incharge ? (
                  <span className="badge" style={{ background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0', fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>
                    ✓ Yes (Incharge)
                  </span>
                ) : (
                  'No'
                )}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">District In-Charge</span>
              <span>
                {detail.is_district_incharge ? (
                  <span className="badge" style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #FDE68A', fontWeight: 700, padding: '2px 8px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <MdStar size={13} color="#D97706" /> Yes (District In-Charge)
                  </span>
                ) : (
                  'No'
                )}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Registered</span>
              <span>{formatDate(detail.created_at || detail.createdAt)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Terms accepted</span>
              <span>{detail.terms_accepted ? 'Yes' : 'No'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Police case?</span>
              <span>{detail.has_police_case ? `Yes — ${detail.police_case_details || ''}` : 'No'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Written elsewhere?</span>
              <span>{detail.has_prior_writing ? `Yes — ${detail.prior_writing_details || ''}` : 'No'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Contributor Type</span>
              <span>
                <select
                  value={detail.is_standard ? 'standard' : 'normal'}
                  onChange={(e) => handleContributorTypeChange(detail.id, e.target.value === 'standard')}
                  disabled={updatingTypeId === detail.id}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 600,
                    border: detail.is_standard ? '1.5px solid #10B981' : '1.5px solid #CBD5E1',
                    background: detail.is_standard ? '#ECFDF5' : '#FFFFFF',
                    color: detail.is_standard ? '#047857' : '#475569',
                    cursor: 'pointer',
                  }}
                >
                  <option value="normal">Normal Contributor</option>
                  <option value="standard">Standard Contributor</option>
                </select>
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Submissions</span>
              <span>{detail.submission_count ?? 0}</span>
            </div>

            {/* KYC Information Section */}
            <div style={{
              gridColumn: '1 / -1',
              marginTop: 12,
              padding: '20px 24px',
              borderRadius: 12,
              background: 'var(--bg)',
              border: '1px solid var(--border)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  🛡️ KYC Information
                </span>
                <span className="badge" style={{
                  background: detail.kyc_verified ? '#ECFDF5' : '#FEF2F2',
                  color: detail.kyc_verified ? '#047857' : '#B91C1C',
                  border: detail.kyc_verified ? '1px solid #A7F3D0' : '1px solid #FECACA',
                  fontWeight: 800,
                  padding: '4px 12px',
                  borderRadius: 6,
                  fontSize: 12,
                }}>
                  KYC STATUS: {detail.kyc_verified ? '✓ Verified' : '✗ Not Verified'}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px 24px' }}>
                {/* AADHAAR */}
                <div style={{ background: 'var(--card)', padding: '16px 18px', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>
                    AADHAAR
                  </div>
                  {detail.is_aadhaar_verified || detail.kyc_verified ? (
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
                      {detail.aadhaar_number || '-'}
                    </div>
                  ) : (
                    <div style={{ marginBottom: 6 }}>
                      <input
                        type="text"
                        placeholder="Enter Aadhaar (12 digits)"
                        value={manualAadhaar}
                        onChange={(e) => setManualAadhaar(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          fontSize: 13,
                          fontWeight: 600,
                          fontFamily: 'monospace',
                          borderRadius: 6,
                          border: '1px solid var(--border)',
                          background: 'var(--bg)',
                          color: 'var(--text)',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  )}
                  <div>
                    {detail.is_aadhaar_verified ? (
                      <span className="badge" style={{ background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0', fontSize: '11px', padding: '2px 8px', fontWeight: 700 }}>
                        ✓ Verified
                      </span>
                    ) : (
                      <span className="badge" style={{ background: '#f1f5f9', color: '#64748b', fontSize: '11px', padding: '2px 8px' }}>
                        Not Verified
                      </span>
                    )}
                  </div>
                </div>

                {/* PAN */}
                <div style={{ background: 'var(--card)', padding: '16px 18px', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>
                    PAN
                  </div>
                  {detail.is_pan_verified || detail.kyc_verified ? (
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
                      {detail.pan_number || '-'}
                    </div>
                  ) : (
                    <div style={{ marginBottom: 6 }}>
                      <input
                        type="text"
                        placeholder="Enter PAN (e.g. CLHPC2704F)"
                        value={manualPan}
                        onChange={(e) => setManualPan(e.target.value.toUpperCase())}
                        maxLength={10}
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          fontSize: 13,
                          fontWeight: 600,
                          fontFamily: 'monospace',
                          textTransform: 'uppercase',
                          borderRadius: 6,
                          border: '1px solid var(--border)',
                          background: 'var(--bg)',
                          color: 'var(--text)',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  )}
                  {detail.pan_name && (
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, fontWeight: 500 }}>
                      Name on PAN: {detail.pan_name}
                    </div>
                  )}
                  <div>
                    {detail.is_pan_verified ? (
                      <span className="badge" style={{ background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0', fontSize: '11px', padding: '2px 8px', fontWeight: 700 }}>
                        ✓ Verified
                      </span>
                    ) : detail.pan_number ? (
                      <span className="badge" style={{ background: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A', fontSize: '11px', padding: '2px 8px', fontWeight: 600 }}>
                        ⚠ Not Verified
                      </span>
                    ) : (
                      <span className="badge" style={{ background: '#f1f5f9', color: '#64748b', fontSize: '11px', padding: '2px 8px' }}>
                        Not Submitted
                      </span>
                    )}
                  </div>
                  {!detail.is_pan_verified && detail.pan_rejection_reason && (
                    <div style={{ fontSize: 11.5, color: '#DC2626', marginTop: 6, fontWeight: 600, lineHeight: 1.4, background: '#FEF2F2', padding: '4px 8px', borderRadius: 6, border: '1px solid #FECACA' }}>
                      Reason: {detail.pan_rejection_reason}
                    </div>
                  )}
                </div>
              </div>

              {/* If KYC Verified: Show Verification Method and Approver details */}
              {detail.kyc_verified && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed var(--border)', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  <div>
                    <strong>Verification Method:</strong>{' '}
                    {detail.kyc_method === 'manual' ? (
                      <span style={{ color: '#047857', fontWeight: 700 }}>Manual Admin Approval</span>
                    ) : (
                      <span style={{ color: '#047857', fontWeight: 700 }}>Automated Verification</span>
                    )}
                  </div>
                  {detail.kyc_method === 'manual' && (
                    <>
                      {detail.kyc_approved_by && (
                        <div>
                          <strong>Approved By:</strong> <span style={{ color: 'var(--text)', fontWeight: 700 }}>{detail.kyc_approved_by}</span>
                        </div>
                      )}
                      {detail.kyc_approved_at && (
                        <div>
                          <strong>Approved At:</strong> <span style={{ color: 'var(--text)' }}>{formatDate(detail.kyc_approved_at)}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* If KYC NOT Verified: Show Approve KYC Action Button */}
              {!detail.kyc_verified && (
                <div style={{
                  marginTop: 14,
                  paddingTop: 12,
                  borderTop: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 12,
                }}>
                  <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', maxWidth: 380, lineHeight: 1.4 }}>
                    Legitimate name or document mismatch? Admin can enter/review documents and approve KYC manually.
                  </div>
                  <button
                    type="button"
                    onClick={handleManualKycClick}
                    disabled={busy || approvingKyc}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      background: 'linear-gradient(135deg, #10B981, #059669)',
                      color: '#FFF',
                      border: 'none',
                      padding: '8px 18px',
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: 'pointer',
                      boxShadow: '0 2px 6px rgba(16, 185, 129, 0.28)',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <MdCheckCircle size={17} /> Approve KYC
                  </button>
                </div>
              )}
            </div>
          </div>

          {detail.status === 'rejected' && detail.rejection_reason && (
            <div className="detail-row" style={{ marginTop: 16 }}>
              <span className="detail-label">Rejection reason</span>
              <span style={{ color: '#EF233C', fontWeight: 600 }}>{detail.rejection_reason}</span>
            </div>
          )}

          {/* Wallet snapshot */}
          {walletStats && (
            <div className="card" style={{ marginTop: 20, background: 'var(--bg)', padding: '20px 24px', borderRadius: 12, border: '1px solid var(--border)' }}>
              <h4 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 16px 0', fontSize: 14, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <MdAccountBalanceWallet size={18} color="#2563EB" /> Wallet Overview
              </h4>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '16px 24px',
                }}
              >
                <div className="detail-row">
                  <span className="detail-label">Income till date</span>
                  <span>{formatINR(walletStats.income_till_date ?? 0)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Available balance</span>
                  <span>{formatINR(walletStats.available_balance ?? 0)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Accepted articles</span>
                  <span>{walletStats.accepted ?? 0}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Rejected articles</span>
                  <span>{walletStats.rejected ?? detail.rejected_articles?.length ?? 0}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '18px 32px', borderTop: '1px solid var(--border)', flexShrink: 0, background: 'var(--card)' }}>
          {onApprove && detail.status === 'pending' && (
            <>
              {onReject && (
                <button className="btn btn-danger" disabled={busy} onClick={() => onReject(detail)}>
                  <MdCancel /> Reject
                </button>
              )}
              <button className="btn btn-primary" disabled={busy} onClick={() => onApprove(detail.id)}>
                <MdCheckCircle /> Approve
              </button>
            </>
          )}

          {onSuspend && detail.status === 'approved' && (
            <button
              className="btn"
              disabled={busy}
              onClick={() => onSuspend(detail)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: '#FFF7ED',
                color: '#C2410C',
                border: '1px solid #FFEDD5',
                borderRadius: 8,
                padding: '8px 16px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <MdBlock size={18} /> Suspend Contributor
            </button>
          )}

          {onActivate && detail.status === 'suspended' && (
            <button
              className="btn btn-primary"
              disabled={busy}
              onClick={() => onActivate(detail.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'linear-gradient(135deg, #10B981, #059669)',
                color: '#FFF',
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <MdCheckCircle size={18} /> Activate Contributor
            </button>
          )}

          <button className="btn btn-ghost" onClick={onClose} style={{ padding: '8px 18px' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
